/**
 * Os dois eixos de uma credencial da agência — a fonte única dos rótulos das duas telas.
 *
 * Até 2026-09-03 a tela de usuários tinha **dois** campos de permissão: uma "Função"
 * (`admin`/`editor`/`viewer`) que não decidia escrita nenhuma, e uma marca invisível de
 * somente leitura que decidia. Eles viraram um eixo só, `access_level`, e o que sobrou do
 * outro (`access_type`) responde a outra pergunta: de que lado do balcão a pessoa está.
 *
 * Mora em `lib/` porque a tela de usuários e a de detalhe do time exibem o mesmo rótulo, e
 * duas cópias divergiriam na primeira renomeação.
 */

/** De que lado do balcão: funcionário da agência ou pessoa da loja. */
export type AccessType = "team" | "client";

/** O que a credencial pode fazer. */
export type AccessLevel = "admin" | "editor" | "viewer";

/** O rótulo de cada nível na tela. */
export const ROTULO_DO_NIVEL: Record<AccessLevel, string> = {
  admin: "Administrador",
  editor: "Editor",
  viewer: "Leitor",
};

/** O que cada nível quer dizer, em uma linha, sob o seletor. */
export const EXPLICACAO_DO_NIVEL: Record<AccessLevel, string> = {
  admin: "Vê, edita e administra os usuários e os times.",
  editor: "Vê e edita os dados das lojas que alcança.",
  viewer: "Só acompanha os painéis; não altera nada.",
};

/**
 * O nível cru da API, estreitado. Qualquer coisa fora dos três cai em `viewer` — a dúvida
 * cai do lado que nega, como no servidor.
 */
export const normalizeAccessLevel = (nivel: string): AccessLevel =>
  nivel === "admin" || nivel === "editor" ? nivel : "viewer";

/**
 * O nível com que cada natureza de credencial nasce.
 *
 * Espelha `nivelPadraoDe` do servidor: o cliente da loja nasce `viewer` porque contratou a
 * agência justamente para não administrar isso; o funcionário nasce `editor` porque foi
 * contratado para operar. Nos dois casos quem administra a conta pode mudar.
 */
export const nivelPadraoDe = (tipo: AccessType): AccessLevel =>
  tipo === "client" ? "viewer" : "editor";

/**
 * Os níveis oferecidos para cada natureza.
 *
 * `admin` some para a credencial de cliente: administrar a equipe e os times é da agência, e
 * é a única combinação dos dois eixos que o servidor recusa. Esconder a opção evita oferecer
 * um `422`.
 */
export const niveisDe = (tipo: AccessType): readonly AccessLevel[] =>
  tipo === "client" ? ["viewer", "editor"] : ["viewer", "editor", "admin"];
